import { useEffect, useState, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  Trophy,
  Layers,
  Target,
  Zap,
  Loader2,
  AlertCircle,
  Medal,
  Crown,
  TrendingUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api';

type LeaderboardCategory = 'collectors' | 'events' | 'props';

interface CollectorEntry {
  rank: number;
  userId: string;
  username: string;
  level: number;
  totalCards: number;
  collectionValue: number;
  rarityCounts: Record<string, number>;
}

interface EventEntry {
  rank: number;
  userId: string;
  username: string;
  level: number;
  eventsPlayed: number;
  wins: number;
  top3Finishes: number;
  top10Finishes: number;
  totalPrizesWon: number;
  totalPoints: number;
}

interface PropEntry {
  rank: number;
  userId: string;
  username: string;
  level: number;
  totalProps: number;
  resolvedProps: number;
  wins: number;
  losses: number;
  winRate: number;
}

type LeaderboardEntry = CollectorEntry | EventEntry | PropEntry;

const CATEGORIES: { id: LeaderboardCategory; label: string; icon: typeof Trophy; description: string }[] = [
  {
    id: 'collectors',
    label: 'Top Collectors',
    icon: Layers,
    description: 'Ranked by collection value',
  },
  {
    id: 'events',
    label: 'Event Champions',
    icon: Trophy,
    description: 'Ranked by wins & placements',
  },
  {
    id: 'props',
    label: 'Prop Masters',
    icon: Target,
    description: 'Ranked by prediction accuracy',
  },
];

const RARITY_COLORS: Record<string, string> = {
  CORE: 'text-warm-gray',
  RARE: 'text-blue-400',
  ELITE: 'text-purple-400',
  LEGENDARY: 'text-gold',
  PRIME: 'text-red-400',
};

export default function LeaderboardsPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    }>
      <LeaderboardsPageContent />
    </Suspense>
  );
}

function LeaderboardsPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentCategory = (searchParams.get('category') || 'collectors') as LeaderboardCategory;

  useEffect(() => {
    async function fetchLeaderboard() {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/api/leaderboards?category=${currentCategory}&limit=50`);

        if (!response.ok) {
          throw new Error('Failed to fetch leaderboard');
        }

        const data = await response.json();
        setLeaderboard(data.data?.leaderboard || []);
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err);
        setError('Failed to load leaderboard');
      } finally {
        setIsLoading(false);
      }
    }

    fetchLeaderboard();
  }, [currentCategory]);

  const setCategory = (category: LeaderboardCategory) => {
    router.push(`/leaderboards?category=${category}`);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-5 w-5 text-gold" />;
      case 2:
        return <Medal className="h-5 w-5 text-zinc-300" />;
      case 3:
        return <Medal className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="text-surface/60 font-mono text-sm w-5 text-center">{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gold/10 border-gold/30';
      case 2:
        return 'bg-zinc-400/10 border-zinc-400/30';
      case 3:
        return 'bg-amber-700/10 border-amber-700/30';
      default:
        return 'bg-parchment border-parchment-border';
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-4xl mx-auto px-4 pt-6 sm:pt-8" />

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <span className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4 block">
            Rankings
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream mb-2">Leaderboards</h1>
          <p className="text-sm sm:text-base text-warm-gray">See how you stack up against the competition</p>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = currentCategory === cat.id;

            return (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={cn(
                  'flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border transition-all whitespace-nowrap flex-shrink-0',
                  isActive
                    ? 'bg-gold/10 border-gold/30 text-gold'
                    : 'bg-parchment border-parchment-border text-surface/60 hover:text-surface hover:border-parchment-dark'
                )}
              >
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <div className="text-left">
                  <p className="text-xs sm:text-sm font-medium">{cat.label}</p>
                  <p className="text-[10px] sm:text-xs opacity-60 hidden sm:block">{cat.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-gold animate-spin" />
          </div>
        )}

        {/* Leaderboard Content */}
        {!isLoading && !error && (
          <div className="space-y-3">
            {leaderboard.length === 0 ? (
              <div className="text-center py-20">
                <Trophy className="h-16 w-16 text-surface/40 mx-auto mb-4" />
                <p className="text-warm-gray text-lg">No entries yet</p>
                <p className="text-surface/60 text-sm">Be the first to make the leaderboard!</p>
              </div>
            ) : (
              <>
                {/* Collectors Leaderboard */}
                {currentCategory === 'collectors' &&
                  (leaderboard as CollectorEntry[]).map((entry) => (
                    <div
                      key={entry.userId}
                      className={cn(
                        'flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-colors',
                        getRankBg(entry.rank)
                      )}
                    >
                      <div className="w-7 sm:w-8 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>

                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-bold text-surface truncate text-sm sm:text-base">{entry.username}</p>
                        <p className="text-[10px] sm:text-xs text-surface/60">Level {entry.level}</p>
                      </div>

                      <div className="hidden md:flex items-center gap-3 text-xs">
                        {Object.entries(entry.rarityCounts).map(
                          ([rarity, count]) =>
                            count > 0 && (
                              <span key={rarity} className={RARITY_COLORS[rarity]}>
                                {count} {rarity.charAt(0)}
                              </span>
                            )
                        )}
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-base sm:text-lg font-bold text-gold">
                          {entry.collectionValue.toLocaleString()}
                        </p>
                        <p className="text-[10px] sm:text-xs text-surface/60">{entry.totalCards} cards</p>
                      </div>
                    </div>
                  ))}

                {/* Events Leaderboard */}
                {currentCategory === 'events' &&
                  (leaderboard as EventEntry[]).map((entry) => (
                    <div
                      key={entry.userId}
                      className={cn(
                        'flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-colors',
                        getRankBg(entry.rank)
                      )}
                    >
                      <div className="w-7 sm:w-8 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>

                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-bold text-surface truncate text-sm sm:text-base">{entry.username}</p>
                        <p className="text-[10px] sm:text-xs text-surface/60">
                          <span className="sm:hidden">{entry.wins}W · {entry.eventsPlayed}P</span>
                          <span className="hidden sm:inline">Level {entry.level}</span>
                        </p>
                      </div>

                      <div className="hidden md:flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="font-bold text-gold">{entry.wins}</p>
                          <p className="text-xs text-surface/60">Wins</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-zinc-300">{entry.top3Finishes}</p>
                          <p className="text-xs text-surface/60">Top 3</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-warm-gray">{entry.eventsPlayed}</p>
                          <p className="text-xs text-surface/60">Played</p>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-base sm:text-lg font-bold text-verdigris">
                          {entry.totalPrizesWon.toLocaleString()}
                        </p>
                        <p className="text-[10px] sm:text-xs text-surface/60">prizes won</p>
                      </div>
                    </div>
                  ))}

                {/* Props Leaderboard */}
                {currentCategory === 'props' &&
                  (leaderboard as PropEntry[]).map((entry) => (
                    <div
                      key={entry.userId}
                      className={cn(
                        'flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-colors',
                        getRankBg(entry.rank)
                      )}
                    >
                      <div className="w-7 sm:w-8 flex justify-center flex-shrink-0">{getRankIcon(entry.rank)}</div>

                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-bold text-surface truncate text-sm sm:text-base">{entry.username}</p>
                        <p className="text-[10px] sm:text-xs text-surface/60">
                          <span className="sm:hidden">{entry.wins}W · {entry.losses}L</span>
                          <span className="hidden sm:inline">Level {entry.level}</span>
                        </p>
                      </div>

                      <div className="hidden md:flex items-center gap-4 text-sm">
                        <div className="text-center">
                          <p className="font-bold text-verdigris">{entry.wins}</p>
                          <p className="text-xs text-surface/60">Wins</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-red-400">{entry.losses}</p>
                          <p className="text-xs text-surface/60">Losses</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-warm-gray">{entry.resolvedProps}</p>
                          <p className="text-xs text-surface/60">Total</p>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="text-base sm:text-lg font-bold text-purple-400">{entry.winRate}%</p>
                        <p className="text-[10px] sm:text-xs text-surface/60">win rate</p>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
